import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CATEGORIES, CATEGORY_KEYS, catMeta } from "@/lib/categories";
import { exportCallPdf } from "@/lib/pdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Mic, MicOff, Sparkles, Copy, CheckCircle2, ClipboardPaste, Radio,
  ChevronRight, Save, Circle, Zap, ShieldAlert, MessageSquare, Trash2,
  BookmarkPlus, FileText, Play, Square,
} from "lucide-react";
import { toast } from "sonner";

function fmtTime(s) {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const sec = (s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

export default function Copilot() {
  const [funnels, setFunnels] = useState([]);
  const [funnelId, setFunnelId] = useState(null);
  const [stageIdx, setStageIdx] = useState(0);
  const [library, setLibrary] = useState([]);

  const [leadName, setLeadName] = useState("");
  const [platform, setPlatform] = useState("Google Meet");
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const [callStarted, setCallStarted] = useState(false);

  const [objections, setObjections] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [responses, setResponses] = useState({}); // objectionId -> {short_cue, acknowledge, isolate, argument}
  const [generatingId, setGeneratingId] = useState(null);

  const [transcript, setTranscript] = useState("");
  const [listening, setListening] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [checked, setChecked] = useState({});
  const [savedToLib, setSavedToLib] = useState({});
  const [callSummary, setCallSummary] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);

  const recognitionRef = useRef(null);

  useEffect(() => {
    api.get("/funnels").then((r) => {
      setFunnels(r.data);
      if (r.data[0]) setFunnelId(r.data[0].id);
    });
    api.get("/objections").then((r) => setLibrary(r.data));
  }, []);

  useEffect(() => {
    if (!callStarted || !running) return;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [callStarted, running]);

  const funnel = funnels.find((f) => f.id === funnelId);
  const stages = funnel?.stages || [];
  const stage = stages[stageIdx];
  const selected = objections.find((o) => o.id === selectedId);
  const resolvedCount = objections.filter((o) => o.resolved).length;
  const rate = objections.length ? Math.round((resolvedCount / objections.length) * 100) : 0;

  function addObjection(text, category) {
    const id = crypto.randomUUID();
    setObjections((prev) => [{ id, text, category, resolved: false }, ...prev]);
    setSelectedId(id);
    return id;
  }

  function libraryResponseFor(category, text) {
    const candidates = library.filter((l) => l.category === category);
    const pool = candidates.length ? candidates : library;
    if (!pool.length) return null;
    const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9 ]/g, " ");
    const words = new Set(norm(text).split(/\s+/).filter((w) => w.length > 3));
    let best = pool[0], bestScore = -1;
    pool.forEach((item) => {
      const hay = norm(`${item.trigger} ${item.argument}`).split(/\s+/);
      let overlap = 0;
      hay.forEach((w) => { if (words.has(w)) overlap++; });
      const score = overlap * 10 + (item.effectiveness || 0) + (item.category === category ? 5 : 0);
      if (score > bestScore) { bestScore = score; best = item; }
    });
    return {
      _source: "biblioteca",
      effectiveness: best.effectiveness,
      short_cue: (best.acknowledge || best.argument || "").split(".")[0].trim(),
      acknowledge: best.acknowledge,
      isolate: best.isolate,
      argument: best.argument,
    };
  }

  useEffect(() => {
    if (!selectedId) return;
    const o = objections.find((x) => x.id === selectedId);
    if (o && !responses[o.id]) {
      const lib = libraryResponseFor(o.category);
      if (lib) setResponses((r) => ({ ...r, [o.id]: lib }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  async function generateFor(obj) {
    setGeneratingId(obj.id);
    try {
      const { data } = await api.post("/ai/generate-response", {
        objection_text: obj.text,
        category: obj.category,
      });
      setResponses((r) => ({ ...r, [obj.id]: data }));
      if (data.category && data.category !== obj.category) {
        setObjections((prev) => prev.map((o) => (o.id === obj.id ? { ...o, category: data.category } : o)));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha na IA");
    } finally {
      setGeneratingId(null);
    }
  }

  async function saveToLibrary(obj) {
    const resp = responses[obj.id];
    if (!resp) return;
    try {
      await api.post("/objections", {
        category: obj.category,
        trigger: obj.text,
        acknowledge: resp.acknowledge || "",
        isolate: resp.isolate || "",
        argument: resp.argument || "",
        effectiveness: 1,
      });
      setSavedToLib((s) => ({ ...s, [obj.id]: true }));
      const lib = await api.get("/objections");
      setLibrary(lib.data);
      toast.success("Resposta guardada na biblioteca da equipa");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao guardar");
    }
  }

  function quickAdd(catKey) {
    if (!callStarted) {
      toast.error("Inicie a chamada primeiro.");
      return;
    }
    const id = addObjection(CATEGORIES[catKey].label, catKey);
    const lib = libraryResponseFor(catKey);
    if (lib) {
      setResponses((r) => ({ ...r, [id]: lib }));
    } else {
      generateFor({ id, text: CATEGORIES[catKey].label, category: catKey });
    }
  }

  async function detect() {
    if (!callStarted) {
      toast.error("Inicie a chamada primeiro.");
      return;
    }
    if (!transcript.trim()) return;
    setDetecting(true);
    try {
      const { data } = await api.post("/ai/detect", { transcript });
      if (!data.length) {
        toast.info("Nenhuma objeção detetada na transcrição.");
      } else {
        data.forEach((d) => addObjection(d.text, d.category));
        toast.success(`${data.length} objeção(ões) detetada(s)`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao analisar");
    } finally {
      setDetecting(false);
    }
  }

  function toggleMic() {
    if (!callStarted) {
      toast.error("Inicie a chamada primeiro.");
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("O seu navegador não suporta reconhecimento de voz. Use 'Colar transcrição'.");
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const rec = new SR();
    rec.lang = "pt-PT";
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      let finalText = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalText += e.results[i][0].transcript + " ";
      }
      if (finalText) setTranscript((t) => (t + " " + finalText).trim());
    };
    rec.onerror = () => { setListening(false); };
    rec.onend = () => { setListening(false); };
    rec.start();
    recognitionRef.current = rec;
    setListening(true);
    toast.success("A ouvir… fale normalmente na chamada.");
  }

  function copy(text) {
    navigator.clipboard.writeText(text);
    toast.success("Copiado");
  }

  function toggleResolved(id) {
    setObjections((prev) => prev.map((o) => (o.id === id ? { ...o, resolved: !o.resolved } : o)));
  }

  function removeObjection(id) {
    setObjections((prev) => prev.filter((o) => o.id !== id));
    if (selectedId === id) setSelectedId(null);
  }

  function startCall() {
    setCallStarted(true);
    setRunning(true);
    setSeconds(0);
    toast.success("Chamada iniciada");
  }

  function endCall() {
    try { recognitionRef.current?.stop(); } catch (_e) { /* noop */ }
    setListening(false);
    setCallStarted(false);
    setRunning(false);
    setSeconds(0);
    setObjections([]);
    setResponses({});
    setSelectedId(null);
    setSavedToLib({});
    setTranscript("");
    setChecked({});
    setLeadName("");
    toast.info("Chamada terminada");
  }

  async function saveCall() {
    if (!callStarted) return toast.error("Inicie a chamada primeiro.");
    if (!leadName.trim()) return toast.error("Indique o nome do lead/cliente.");
    setSaving(true);
    try {
      const { data } = await api.post("/calls", {
        lead_name: leadName,
        funnel_id: funnelId,
        platform,
        duration_seconds: seconds,
        objections: objections.map((o) => ({ text: o.text, category: o.category, resolved: o.resolved })),
      });
      toast.success("Chamada guardada no histórico");
      if (data.summary) {
        setCallSummary(data);
        setSummaryOpen(true);
      }
      setObjections([]); setResponses({}); setSelectedId(null); setSavedToLib({});
      setSeconds(0); setLeadName(""); setTranscript(""); setChecked({});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao guardar");
    } finally {
      setSaving(false);
    }
  }

  const libraryMatches = selected
    ? library.filter((l) => l.category === selected.category).slice(0, 2)
    : [];

  return (
    <div className="space-y-4">
      {/* HUD */}
      <div className="rounded-2xl border border-white/10 bg-[#111827] p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          {callStarted ? (
            <>
              <span className="recording-pulse w-2.5 h-2.5 rounded-full bg-[#EF4444]" />
              <span className="cue-label text-[#EF4444]" data-testid="call-status-label">Chamada Ativa</span>
            </>
          ) : (
            <>
              <span className="w-2.5 h-2.5 rounded-full bg-[#475569]" />
              <span className="cue-label text-[#64748B]" data-testid="call-status-label">Sem chamada</span>
            </>
          )}
        </div>
        <div className="font-mono text-2xl font-semibold tabular-nums" data-testid="call-timer">
          {fmtTime(seconds)}
        </div>
        <Input
          data-testid="lead-name-input"
          value={leadName}
          onChange={(e) => setLeadName(e.target.value)}
          placeholder="Nome do lead / cliente"
          className="w-48 h-9 bg-[#0B0F17] border-white/10"
          disabled={!callStarted}
        />
        <select
          data-testid="platform-select"
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          className="h-9 rounded-md bg-[#0B0F17] border border-white/10 px-3 text-sm disabled:opacity-50"
          disabled={!callStarted}
        >
          <option>Google Meet</option>
          <option>Zoom</option>
          <option>Teams</option>
          <option>Presencial</option>
        </select>
        <div className="flex-1" />
        <div className="flex items-center gap-4">
          <div className="text-center">
            <div className="cue-label text-[#64748B]">Objeções</div>
            <div className="font-mono text-lg font-semibold">{objections.length}</div>
          </div>
          <div className="text-center">
            <div className="cue-label text-[#64748B]">Superadas</div>
            <div className="font-mono text-lg font-semibold text-[#10B981]">{rate}%</div>
          </div>
          {!callStarted ? (
            <Button
              onClick={startCall}
              data-testid="start-call-button"
              className="bg-[#10B981] hover:bg-[#059669] h-9"
            >
              <Play className="w-4 h-4 mr-1.5" /> Iniciar chamada
            </Button>
          ) : (
            <>
              <Button
                onClick={() => setRunning((r) => !r)}
                variant="outline"
                className="border-white/10 bg-transparent h-9"
                data-testid="toggle-timer-button"
              >
                {running ? "Pausar" : "Retomar"}
              </Button>
              <Button
                onClick={endCall}
                variant="outline"
                className="border-[#EF4444]/40 bg-transparent text-[#EF4444] hover:bg-[#EF4444]/10 h-9"
                data-testid="end-call-button"
              >
                <Square className="w-4 h-4 mr-1.5" /> Terminar
              </Button>
            </>
          )}
          <Button
            onClick={saveCall}
            disabled={saving || !callStarted || !leadName.trim()}
            data-testid="save-call-summary-button"
            className="bg-[#2563EB] hover:bg-[#1D4ED8] h-9"
          >
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "A guardar…" : "Guardar chamada"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* LEFT: Funnel & Script */}
        <div className="lg:col-span-3 rounded-2xl border border-white/10 bg-[#111827] p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Radio className="w-4 h-4 text-[#2563EB]" />
            <h3 className="font-display font-bold text-base">Guião & Funil</h3>
          </div>
          {funnels.length > 1 && (
            <select
              value={funnelId || ""}
              onChange={(e) => { setFunnelId(e.target.value); setStageIdx(0); }}
              className="w-full h-9 rounded-md bg-[#0B0F17] border border-white/10 px-3 text-sm"
              data-testid="funnel-select"
            >
              {funnels.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          )}
          <div className="flex flex-wrap gap-1.5">
            {stages.map((s, i) => (
              <button
                key={s.id}
                data-testid={`funnel-stage-tab-${i}`}
                onClick={() => setStageIdx(i)}
                className={`text-xs px-2.5 py-1.5 rounded-lg font-medium transition-colors ${
                  i === stageIdx ? "bg-[#2563EB] text-white" : "bg-white/5 text-[#94A3B8] hover:text-white"
                }`}
              >
                {i + 1}. {s.title.split(" ")[0]}
              </button>
            ))}
          </div>
          {stage && (
            <div className="fade-up space-y-3">
              <div>
                <div className="cue-label text-[#64748B] mb-1">Fase Atual</div>
                <div className="font-display font-bold text-lg">{stage.title}</div>
                <p className="text-sm text-[#94A3B8] mt-1">{stage.objective}</p>
              </div>
              <div>
                <div className="cue-label text-[#64748B] mb-2">Perguntas-chave</div>
                <div className="space-y-2">
                  {stage.questions.map((q, qi) => {
                    const key = `${stageIdx}-${qi}`;
                    return (
                      <button
                        key={qi}
                        onClick={() => setChecked((c) => ({ ...c, [key]: !c[key] }))}
                        className="w-full text-left flex items-start gap-2 group"
                        data-testid={`stage-question-${qi}`}
                      >
                        {checked[key] ? (
                          <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" />
                        ) : (
                          <Circle className="w-4 h-4 text-[#475569] mt-0.5 shrink-0 group-hover:text-[#2563EB]" />
                        )}
                        <span className={`text-sm ${checked[key] ? "text-[#64748B] line-through" : "text-[#F8FAFC]"}`}>
                          {q}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="rounded-lg bg-[#0B0F17] border border-white/5 p-3">
                <div className="cue-label text-[#64748B] mb-1">Critério para avançar</div>
                <p className="text-xs text-[#94A3B8]">{stage.exit_criteria}</p>
              </div>
              {stageIdx < stages.length - 1 && (
                <Button
                  onClick={() => setStageIdx((i) => Math.min(i + 1, stages.length - 1))}
                  variant="outline"
                  className="w-full border-[#2563EB]/40 bg-transparent text-[#2563EB] hover:bg-[#2563EB]/10"
                  data-testid="next-stage-button"
                >
                  Avançar de fase <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          )}
        </div>

        {/* CENTER: Intake */}
        <div className="lg:col-span-5 space-y-4">
          <div className="rounded-2xl border border-white/10 bg-[#111827] p-5 space-y-4">
            {!callStarted && (
              <div className="rounded-lg border border-[#F59E0B]/30 bg-[#F59E0B]/5 px-3 py-2 text-xs text-[#F59E0B]" data-testid="call-not-started-hint">
                Clique em <b>Iniciar chamada</b> no topo para ativar a deteção de objeções.
              </div>
            )}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-[#F59E0B]" />
                <h3 className="font-display font-bold text-base">Deteção de Objeções</h3>
              </div>
              <Button
                onClick={toggleMic}
                disabled={!callStarted}
                data-testid="mic-toggle-button"
                className={`h-9 ${listening ? "bg-[#EF4444] hover:bg-[#dc2626] recording-pulse" : "bg-[#1A2234] hover:bg-[#232d42]"} disabled:opacity-50`}
              >
                {listening ? <MicOff className="w-4 h-4 mr-1.5" /> : <Mic className="w-4 h-4 mr-1.5" />}
                {listening ? "A ouvir…" : "Ouvir chamada"}
              </Button>
            </div>

            {/* Quick palette */}
            <div>
              <div className="cue-label text-[#64748B] mb-2">Palete rápida — clique ao ouvir a objeção</div>
              <div className={`flex flex-wrap gap-2 ${!callStarted ? "opacity-50 pointer-events-none" : ""}`}>
                {CATEGORY_KEYS.map((k) => (
                  <button
                    key={k}
                    data-testid={`quick-objection-badge-${k}`}
                    onClick={() => quickAdd(k)}
                    disabled={!callStarted}
                    className="px-3 py-2 rounded-lg text-xs font-semibold border transition-transform hover:scale-105"
                    style={{ color: CATEGORIES[k].color, backgroundColor: CATEGORIES[k].bg, borderColor: CATEGORIES[k].color + "40" }}
                  >
                    {CATEGORIES[k].short}
                  </button>
                ))}
              </div>
            </div>

            {/* Transcript */}
            <div>
              <Textarea
                data-testid="transcript-text-input"
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                placeholder="Cole aqui a transcrição do Google Meet / Zoom (ou use o microfone) e clique em 'Detetar objeções'…"
                className="min-h-[110px] bg-[#0B0F17] border-white/10 text-sm resize-none"
                disabled={!callStarted}
              />
              <div className="flex gap-2 mt-2">
                <Button
                  onClick={detect}
                  disabled={detecting || !callStarted || !transcript.trim()}
                  data-testid="detect-objections-button"
                  className="bg-[#2563EB] hover:bg-[#1D4ED8] h-9"
                >
                  <Sparkles className="w-4 h-4 mr-1.5" /> {detecting ? "A analisar…" : "Detetar objeções (IA)"}
                </Button>
                {transcript && (
                  <Button onClick={() => setTranscript("")} variant="ghost" className="h-9 text-[#94A3B8]">
                    Limpar
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Objection list */}
          <div className="rounded-2xl border border-white/10 bg-[#111827] p-5">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-4 h-4 text-[#2563EB]" />
              <h3 className="font-display font-bold text-base">Objeções desta chamada</h3>
            </div>
            {objections.length === 0 ? (
              <p className="text-sm text-[#64748B] py-6 text-center">
                Nenhuma objeção ainda. Use a palete rápida, o microfone ou cole a transcrição.
              </p>
            ) : (
              <div className="space-y-2">
                {objections.map((o) => {
                  const m = catMeta(o.category);
                  const active = o.id === selectedId;
                  return (
                    <div
                      key={o.id}
                      onClick={() => setSelectedId(o.id)}
                      data-testid={`objection-item-${o.id}`}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        active ? "border-[#2563EB] bg-[#2563EB]/10" : "border-white/10 bg-[#0B0F17] hover:border-white/20"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ color: m.color, backgroundColor: m.bg }}>
                            {m.short}
                          </span>
                          <p className={`text-sm mt-1 ${o.resolved ? "line-through text-[#64748B]" : "text-[#F8FAFC]"}`}>{o.text}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleResolved(o.id); }}
                            data-testid={`mark-resolved-button-${o.id}`}
                            title="Marcar como superada"
                          >
                            <CheckCircle2 className={`w-5 h-5 ${o.resolved ? "text-[#10B981]" : "text-[#475569] hover:text-[#10B981]"}`} />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); removeObjection(o.id); }} title="Remover">
                            <Trash2 className="w-4 h-4 text-[#475569] hover:text-[#EF4444]" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Battlecards */}
        <div className="lg:col-span-4">
          <div className="rounded-2xl border border-white/10 bg-[#111827] p-5 lg:sticky lg:top-24">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-4 h-4 text-[#10B981]" fill="#10B981" />
              <h3 className="font-display font-bold text-base">Resposta Imediata</h3>
            </div>

            {!selected ? (
              <div className="py-12 text-center">
                <Sparkles className="w-8 h-8 text-[#334155] mx-auto mb-3" />
                <p className="text-sm text-[#64748B]">
                  Selecione uma objeção para ver a resposta em 3 camadas — reconhecer, isolar e argumentar.
                </p>
              </div>
            ) : (
              <div className="fade-up space-y-4">
                <div className="rounded-lg bg-[#0B0F17] border border-white/10 p-3">
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ color: catMeta(selected.category).color, backgroundColor: catMeta(selected.category).bg }}>
                    {catMeta(selected.category).label}
                  </span>
                  <p className="text-sm text-white mt-2">{selected.text}</p>
                </div>

                {!responses[selected.id] ? (
                  <Button
                    onClick={() => generateFor(selected)}
                    disabled={generatingId === selected.id}
                    data-testid="ai-generate-response-button"
                    className="w-full bg-[#2563EB] hover:bg-[#1D4ED8]"
                  >
                    <Sparkles className="w-4 h-4 mr-1.5" />
                    {generatingId === selected.id ? "A gerar resposta…" : "Gerar resposta com IA"}
                  </Button>
                ) : (
                  <>
                    <div className="rounded-lg p-2.5 border flex items-center gap-2 text-xs" style={responses[selected.id]._source === "biblioteca" ? { borderColor: "rgba(245,158,11,0.35)", backgroundColor: "rgba(245,158,11,0.08)", color: "#F59E0B" } : { borderColor: "rgba(37,99,235,0.35)", backgroundColor: "rgba(37,99,235,0.08)", color: "#93b4fb" }}>
                      {responses[selected.id]._source === "biblioteca" ? (
                        <><Zap className="w-3.5 h-3.5" fill="#F59E0B" color="#F59E0B" /> Sugestão da biblioteca · eficácia {responses[selected.id].effectiveness} <span className="text-[#64748B]">(sem custo de IA)</span></>
                      ) : (
                        <><Sparkles className="w-3.5 h-3.5" /> Resposta gerada por IA</>
                      )}
                    </div>
                    <div className="rounded-lg p-3 border border-[#10B981]/30 bg-[#10B981]/5">
                      <div className="cue-label text-[#10B981] mb-1">Diga já isto</div>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-white">{responses[selected.id].short_cue}</p>
                        <button onClick={() => copy(responses[selected.id].short_cue)} data-testid="copy-response-button-cue">
                          <Copy className="w-4 h-4 text-[#64748B] hover:text-white" />
                        </button>
                      </div>
                    </div>
                    {[
                      { k: "acknowledge", n: "1", t: "Reconhecer & Validar" },
                      { k: "isolate", n: "2", t: "Pergunta de Isolamento" },
                      { k: "argument", n: "3", t: "Argumento / Solução" },
                    ].map((layer) => (
                      <div key={layer.k} className="rounded-lg bg-[#0B0F17] border border-white/10 p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="cue-label text-[#64748B]">
                            <span className="text-[#2563EB]">{layer.n}.</span> {layer.t}
                          </div>
                          <button onClick={() => copy(responses[selected.id][layer.k])} data-testid={`copy-response-button-${layer.k}`}>
                            <Copy className="w-3.5 h-3.5 text-[#64748B] hover:text-white" />
                          </button>
                        </div>
                        <p className="text-sm text-[#F8FAFC] leading-relaxed">{responses[selected.id][layer.k]}</p>
                      </div>
                    ))}
                    {responses[selected.id]._source !== "biblioteca" && (
                      <Button
                        onClick={() => saveToLibrary(selected)}
                        disabled={savedToLib[selected.id]}
                        data-testid="save-to-library-button"
                        className="w-full bg-[#10B981] hover:bg-[#059669] h-9"
                      >
                        <BookmarkPlus className="w-4 h-4 mr-1.5" />
                        {savedToLib[selected.id] ? "Guardada na biblioteca ✓" : "Esta resposta resultou — guardar"}
                      </Button>
                    )}
                    <Button
                      onClick={() => generateFor(selected)}
                      disabled={generatingId === selected.id}
                      data-testid="ai-generate-response-button"
                      variant={responses[selected.id]._source === "biblioteca" ? "default" : "ghost"}
                      className={responses[selected.id]._source === "biblioteca"
                        ? "w-full bg-[#2563EB] hover:bg-[#1D4ED8] h-9"
                        : "w-full text-[#94A3B8] h-8 text-xs"}
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                      {generatingId === selected.id
                        ? "A gerar…"
                        : responses[selected.id]._source === "biblioteca"
                        ? "Gerar resposta com IA"
                        : "Gerar outra variação"}
                    </Button>
                  </>
                )}

                {libraryMatches.length > 0 && (
                  <div className="pt-2 border-t border-white/10">
                    <div className="cue-label text-[#64748B] mb-2">Da biblioteca da equipa</div>
                    <div className="space-y-2">
                      {libraryMatches.map((l) => (
                        <div key={l.id} className="rounded-lg bg-[#0B0F17] border border-white/5 p-3">
                          <p className="text-xs text-[#94A3B8]">{l.argument}</p>
                          <div className="flex items-center gap-1 mt-1.5">
                            <Zap className="w-3 h-3 text-[#F59E0B]" fill="#F59E0B" />
                            <span className="text-[10px] font-mono text-[#64748B]">eficácia {l.effectiveness}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Button
                  onClick={() => toggleResolved(selected.id)}
                  variant="outline"
                  className={`w-full border-white/10 bg-transparent ${selected.resolved ? "text-[#10B981]" : "text-white"}`}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />
                  {selected.resolved ? "Superada ✓" : "Marcar como superada"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={summaryOpen} onOpenChange={setSummaryOpen}>
        <DialogContent className="bg-[#111827] border-white/10 max-w-lg" data-testid="call-summary-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#2563EB]" /> Resumo da chamada
            </DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              Gerado por IA com base nas objeções registadas nesta chamada.
            </DialogDescription>
          </DialogHeader>
          {callSummary && (
            <div className="space-y-3">
              <div className="text-xs cue-label text-[#64748B]">Lead: {callSummary.lead_name}</div>
              <p className="text-sm text-[#F8FAFC] leading-relaxed whitespace-pre-line">{callSummary.summary}</p>
              <div className="flex gap-2">
                <Button
                  onClick={() => { navigator.clipboard.writeText(callSummary.summary); toast.success("Resumo copiado"); }}
                  variant="outline"
                  className="flex-1 border-white/10 bg-transparent"
                >
                  <Copy className="w-4 h-4 mr-1.5" /> Copiar
                </Button>
                <Button
                  onClick={() => exportCallPdf(callSummary)}
                  data-testid="summary-export-pdf-button"
                  className="flex-1 bg-[#2563EB] hover:bg-[#1D4ED8]"
                >
                  <FileText className="w-4 h-4 mr-1.5" /> Descarregar PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
